import { Apis } from '@/appSettings/AppSettings'
import { AppLoading } from '@/components/AppLoading'
import { usePartner } from '@/context/PartnerContext'
import { useUrl } from '@/hooks'
import { getPublicEmailAttachmentUrl, getPublicEmailAttachmentZipUrl, useGetPublicEmailContent, usePublicEmailSignOut } from '@/hooks/usePublicEmail'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
  VStack
} from '@chakra-ui/react'
import { faFilePdf, faFileZip } from '@fortawesome/pro-duotone-svg-icons'
import { faArrowRightFromBracket } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AuthPageLayout } from 'BrokerTsLib/src/components'
import { formatDate } from 'BrokerTsLib/src/Utils/formatDate'
import { useEffect } from 'react'
import { useNavigate } from 'react-router'
import { EmailPageLayout } from './EmailPageLayout'

export function EmailPage() {
  const { data: emailContent, isLoading, error } = useGetPublicEmailContent()
  const { currentPartner } = usePartner()
  const { mutateAsync: signOut, isPending: isLoggingOut } = usePublicEmailSignOut()
  const navigate = useNavigate()
  const baseUrl = useUrl(Apis.publicEmail)

  useEffect(() => {
    if (error && (error as any)?.response?.status === 401) {
      // if unauthorized, might need to login again
      navigate('/public/email/login')
    }
  }, [error, navigate])

  if (isLoading)
    return <AppLoading />

  if (!emailContent) {
    return (
      <AuthPageLayout logo={currentPartner?.logo}>
        <Flex alignItems={'center'} p={4} flex={1} justifyContent={'center'}>
          <Alert status='error' variant='left-accent' fontSize={'xl'} p={8} maxW={'600px'}>
            {error ? ((error as any)?.response?.data || 'Chyba při stahování emailu.') : 'Email se nepodařilo načíst.'}
          </Alert>
        </Flex>
      </AuthPageLayout>
    )
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/public/email/logout')
  }

  return (
    <EmailPageLayout elementRight={<Button variant="ghost" onClick={handleSignOut} isLoading={isLoggingOut} py={3}>
      <HStack>
        <Icon as={FontAwesomeIcon} icon={faArrowRightFromBracket} />
        <span>Odhlásit se</span>
      </HStack>
    </Button>}>
      <Alert status="warning" variant="left-accent" display="flex" flexDir="column" alignItems="flex-start" mb={0} w='full'>
        <AlertTitle>POZOR</AlertTitle>
        <AlertDescription>
          Tato stránka i odkazy na dokumenty níže, jsou platné pouze <b>do {formatDate(new Date(emailContent.validTo))}</b>. <br />
          Dokumenty si prosím stáhněte a uložte do svého zabezpečeného úložiště.
        </AlertDescription>
      </Alert>
      <VStack gap={6} w="full" alignItems='stretch'>
        <Heading fontSize="2xl" fontWeight="semibold">{emailContent.subject}</Heading>

        {emailContent.attachments && emailContent.attachments.length > 0 && (
          <Stack gap={2}>
            <Grid gap={2} gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }} w={'full'}>
              <Heading fontSize="lg" fontWeight='semibold' alignContent='center' my={3}>
                Přílohy ({emailContent.attachments.length})
              </Heading>

              {emailContent.attachments.length > 0 && (
                <Button
                  as="a"
                  href={getPublicEmailAttachmentZipUrl(baseUrl ?? '')}
                  variant="outline"
                  p={3}
                  justifyContent='start'
                >
                  <HStack gap={3}>
                    <Icon as={FontAwesomeIcon} icon={faFileZip} size='xl' />
                    <Text sx={{ fontSize: 'md', fontWeight: 'medium' }}>Stáhnout vše jako ZIP</Text>
                  </HStack>
                </Button>
              )}

              {emailContent.attachments.map(a => (
                <LinkBox>
                  <HStack
                    sx={{
                      gap: 3,
                      p: 3,
                      transition: 'all 0.3s',
                      _hover: {
                        bg: 'gray.50',
                        borderRadius: 'sm',
                      }
                    }}>
                    <Icon as={FontAwesomeIcon} icon={faFilePdf} size='xl' />
                    <LinkOverlay href={getPublicEmailAttachmentUrl(a.id, baseUrl ?? '')}>
                      <Text sx={{ fontSize: 'md', fontWeight: 'medium' }}>{a.name}</Text>
                    </LinkOverlay>
                  </HStack>
                </LinkBox>
              ))}
            </Grid>
          </Stack>
        )}

        <Box
          dangerouslySetInnerHTML={{ __html: emailContent.body }}
          w="full"
          __css={{
            ' #box1': {
              padding: '0 !important'
            },
            ' #box2': {
              overflow: 'hidden'
            }
          }}
        />
      </VStack>
    </EmailPageLayout>
  )
}
